import BankLinks from "@/data/BankLinks";
import { mainSidebarLinks } from "@/data/nav";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import React, { useEffect } from "react";

/* ─────────────────────────────────────────────────────────────
   Sidebar navigation (dark theme) + SSE cache invalidation
   ───────────────────────────────────────────────────────────── */
export default function MainNavigation({
  pathname,
  locale,
  openMenu,
  setOpenMenu,
  show,
  userRole,
}: {
  pathname: string;
  locale: string;
  openMenu: string | null;
  setOpenMenu: React.Dispatch<React.SetStateAction<string | null>>;
  show: boolean;
  userRole?: string;
}) {
  const bankSidebarLinks = BankLinks();
  const sidebar = userRole === "bank" ? bankSidebarLinks : mainSidebarLinks;
  const queryClient = useQueryClient();
  const ar = locale === "ar";

  useEffect(() => {
    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_API_URI}/api/v1/notifications/stream`,
    );
    eventSource.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      if (userRole === "bank") {
        if (data.type === "REFRESH_ACCOUNT") await queryClient.invalidateQueries({ queryKey: ["current-user"] });
        if (data.type === "REFRESH_BANK_SUBSCRIPTION") await queryClient.invalidateQueries({ queryKey: ["subscriptionStatus"] });
        if (data.type === "REFRESH_PROGRAM") await queryClient.invalidateQueries({ queryKey: ["mortgagePrograms"] });
        if (data.type === "REFRESH_COMMISSIONS") await queryClient.invalidateQueries({ queryKey: ["bankPayables"] });
        if (data.type === "REFRESH_ACTIONS") await queryClient.invalidateQueries({ queryKey: ["bankAuctions"] });
      }
      // access fee paid / limits changed by admin → refresh profile + dashboard
      if (data.type === "REFRESH_ACCOUNT") {
        await queryClient.invalidateQueries({ queryKey: ["current-user"] });
        await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      }
      if (data.type === "REFRESH_PROPERTIES") {
        await queryClient.invalidateQueries({ queryKey: ["properties"] });
        await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      }
      if (data.type === "REFRESH_NOTIFICATIONS") {
        await queryClient.invalidateQueries({ queryKey: ["dev-notifications"] });
        await queryClient.invalidateQueries({ queryKey: ["dev-notifications-unread"] });
      }
    };
    eventSource.onerror = () => eventSource.close();
    return () => eventSource.close();
  }, [queryClient, userRole]);

  const strip = (p: string) => p.replace(`/${locale}`, "");

  return (
    <ul className="space-y-1">
      {sidebar.map((link: any) => {
        const hasSubMenu = !!link.subMenu;
        const current = strip(pathname);
        const isActive = current === link.href || (link.href && current.startsWith(link.href + "/"));
        const isSubActive = link.subMenu?.some((sub: any) => current.startsWith(sub.href));
        const isOpened = openMenu === link.name_en;
        const label = locale === "en" ? link.name_en : link.name_ar;

        return (
          <li key={link.name_en}>
            {hasSubMenu ? (
              <div className="flex flex-col">
                <button
                  onClick={() => setOpenMenu(isOpened ? null : link.name_en)}
                  title={!show ? label : undefined}
                  className={`group flex w-full items-center justify-between rounded-2xl border px-3 py-2.5 text-[13px] font-bold transition-all duration-200 ${
                    isSubActive || isOpened
                      ? "border-[#d9b25a]/20 bg-[#d9b25a]/10 text-[#d9b25a]"
                      : "border-transparent text-white/50 hover:border-white/[0.05] hover:bg-white/[0.05] hover:text-white/85"
                  } ${!show ? "justify-center" : ""}`}
                >
                  <span className="flex items-center gap-3">
                    <link.icon className="size-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110" />
                    {show && <span>{label}</span>}
                  </span>
                  {show && <ChevronDown className={`size-3.5 transition-transform duration-300 ${isOpened ? "rotate-180" : ""}`} />}
                </button>

                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: isOpened && show ? "320px" : "0px", opacity: isOpened && show ? 1 : 0 }}
                >
                  <ul className={`${ar ? "mr-6 border-r" : "ml-6 border-l"} mt-1 space-y-0.5 border-white/[0.08] py-1`}>
                    {link.subMenu.map((sub: any) => {
                      const activeSub = current.startsWith(sub.href);
                      return (
                        <li key={sub.href}>
                          <Link
                            href={sub.href}
                            className={`relative block rounded-xl px-4 py-2 text-[12px] font-semibold transition-all ${
                              activeSub ? "text-[#d9b25a]" : "text-white/40 hover:bg-white/[0.04] hover:text-white/80"
                            }`}
                          >
                            {activeSub && (
                              <span className={`absolute top-1/2 ${ar ? "-right-[7px]" : "-left-[7px]"} size-1.5 -translate-y-1/2 rounded-full bg-[#d9b25a] shadow-[0_0_8px_#d9b25a]`} />
                            )}
                            {locale === "en" ? sub.name_en : sub.name_ar}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            ) : (
              <Link
                href={link.href}
                title={!show ? label : undefined}
                className={`group relative flex items-center gap-3 rounded-2xl border px-3 py-2.5 text-[13px] font-bold transition-all duration-200 ${
                  isActive
                    ? "border-[#d9b25a]/25 bg-gradient-to-l from-[#0d3589]/60 to-[#d9b25a]/10 text-white shadow-[0_8px_30px_-12px_rgba(217,178,90,0.5)]"
                    : "border-transparent text-white/50 hover:border-white/[0.05] hover:bg-white/[0.05] hover:text-white/85"
                } ${!show ? "justify-center" : ""}`}
              >
                {isActive && (
                  <span className={`absolute top-1/2 ${ar ? "right-0 rounded-l-full" : "left-0 rounded-r-full"} h-[55%] w-[3px] -translate-y-1/2 bg-gradient-to-b from-[#d9b25a] to-[#b8923f]`} />
                )}
                <link.icon className={`size-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110 ${isActive ? "text-[#d9b25a]" : ""}`} />
                {show && <span>{label}</span>}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}
